<template>
  <el-dialog v-model="visible" title="批量导入 SPU 图片" fullscreen :close-on-click-modal="false"
    :close-on-press-escape="!busy" :before-close="requestClose" class="spu-image-import" @closed="reset">
    <div class="import-content">
      <el-alert title="按文件名匹配款号，确认后统一导入。新图追加保留旧图，每组第一张新图作为本次展示图。" type="info" :closable="false" show-icon />
      <el-upload ref="picker" action="#" drag multiple :auto-upload="false" :show-file-list="false" :class="{ 'compact-picker': items.length > 0 }"
        accept=".jpg,.jpeg,.png" :disabled="busy" :on-change="onFileChange">
        <el-icon :size="items.length ? 22 : 32"><UploadFilled /></el-icon>
        <div>将图片拖到这里，或 <em>点击选择图片</em></div>
        <template #tip><div class="el-upload__tip">命名示例：Z00001.jpg、Z00001_01.jpg、自定义款号-02.png；支持 JPG/JPEG/PNG，单张 ≤10 MB，每批 ≤500 张 / 500 MB。</div></template>
      </el-upload>

      <div class="summary-bar">
        <span>已选 <b>{{ items.length }}</b> 张 · 涉及 <b>{{ groups.length }}</b> 个 SPU · 待处理 <b>{{ unresolved.length + invalid.length }}</b> 张</span>
        <span>成功 {{ succeeded.length }} · 失败 {{ failed.length }}</span>
      </div>
      <el-alert v-if="notice" :title="notice" type="warning" :closable="false" show-icon />
      <el-alert v-if="error" :title="error" type="error" :closable="false" show-icon />
      <div v-if="runItems.length" class="batch-progress">
        <el-progress :percentage="overallProgress" :status="!running && failed.length ? 'warning' : undefined" />
        <span>{{ running ? '正在导入' : stopRequested ? '已停止后续上传' : '本次上传结束' }} · {{ runFinished }} / {{ runItems.length }} 张已处理</span>
      </div>

      <div class="filter-bar">
        <el-radio-group v-model="filter">
          <el-radio-button value="all">全部 {{ items.length }}</el-radio-button>
          <el-radio-button value="matched">已匹配 {{ matched.length }}</el-radio-button>
          <el-radio-button value="pending">待指定 {{ unresolved.length }}</el-radio-button>
          <el-radio-button value="invalid">格式错误 {{ invalid.length }}</el-radio-button>
        </el-radio-group>
        <el-button v-if="matchFailures.length" :disabled="busy" @click="matchItems(matchFailures)">重试匹配 {{ matchFailures.length }} 张</el-button>
      </div>

      <section v-if="['all', 'pending', 'invalid'].includes(filter) && exceptionItems.length" class="exception-panel">
        <div class="section-heading">
          <h3>待处理图片</h3>
          <el-button :disabled="busy || !selectedPending.length" @click="openAssign(selectedPending)">将已选 {{ selectedPending.length }} 张指定到同一 SPU</el-button>
        </div>
        <el-table :data="exceptionPage" row-key="clientId" @selection-change="selectedPending = $event.filter(item => item.matchState === 'pending' && !!item.fileId)">
          <el-table-column type="selection" width="44" :selectable="row => !busy && row.matchState === 'pending' && !!row.fileId" />
          <el-table-column label="图片" width="84"><template #default="{ row }"><el-image v-if="row.preview" :src="row.preview" :preview-src-list="[row.preview]" preview-teleported fit="contain" class="small-image" /></template></el-table-column>
          <el-table-column prop="name" label="文件名" min-width="190" show-overflow-tooltip />
          <el-table-column label="原因" min-width="220"><template #default="{ row }">{{ row.matchState === 'matching' ? '正在匹配…' : row.reason }}</template></el-table-column>
          <el-table-column label="操作" width="160"><template #default="{ row }">
            <el-button v-if="row.matchState === 'pending' && row.fileId" link type="primary" :disabled="busy" @click="openAssign([row])">指定 SPU</el-button>
            <el-button link type="danger" :disabled="busy" @click="remove(row)">移除</el-button>
          </template></el-table-column>
        </el-table>
        <el-pagination v-if="exceptionItems.length > pageSize" v-model:current-page="exceptionPageNumber" :page-size="pageSize" :total="exceptionItems.length" layout="prev, pager, next" />
      </section>

      <section v-if="['all', 'matched'].includes(filter)">
        <div v-for="group in groupPage" :key="group.spu.id" class="spu-group">
          <div class="section-heading">
            <div class="spu-heading">
              <el-image v-if="group.spu.thumbnailPath" :src="assetUrl(group.spu.thumbnailPath)" class="small-image" fit="contain" />
              <div><strong>{{ group.spu.productCode }} <span v-if="group.spu.serialCode"> / {{ group.spu.serialCode }}</span></strong><div>{{ group.spu.productName }}</div></div>
            </div>
            <span>已有 {{ group.spu.imageCount }} 张 · 新图 {{ group.images.length }} 张 · 成功 {{ group.images.filter(i => i.uploadState === 'success').length }} · 失败 {{ group.images.filter(i => i.uploadState === 'failed').length }}</span>
          </div>
          <div class="image-grid">
            <div v-for="(item, index) in group.images" :key="item.clientId" class="image-card">
              <el-image :src="item.preview" :preview-src-list="group.images.map(i => i.preview)" :initial-index="index" preview-teleported loading="lazy" fit="contain" class="import-preview" />
              <div class="file-name" :title="item.name">{{ item.name }}</div>
              <el-tag v-if="isDisplayImage(item, group)" size="small" type="warning">本次展示图</el-tag>
              <span class="match-label">{{ matchLabel(item) }}</span>
              <el-progress v-if="item.uploadState === 'uploading'" :percentage="Math.round(item.progress)" :stroke-width="4" />
              <div :class="['upload-status', item.uploadState]">{{ stateLabel(item) }}</div>
              <div v-if="item.error" class="file-error">{{ item.error }}</div>
              <div class="image-actions">
                <el-button link type="primary" :disabled="busy || item.prepared" :title="item.prepared ? '已进入上传队列，关联关系已固定' : ''" @click="openAssign([item])">更改 SPU</el-button>
                <el-button link type="danger" :disabled="busy || item.prepared" @click="remove(item)">移除</el-button>
              </div>
            </div>
          </div>
        </div>
        <el-pagination v-if="groups.length > groupPageSize" v-model:current-page="groupPageNumber" :page-size="groupPageSize" :total="groups.length" layout="total, prev, pager, next" />
      </section>
      <el-empty v-if="!items.length" description="选择图片后，在这里核对匹配结果" />
    </div>

    <template #footer>
      <div class="import-footer">
        <span>待指定和格式错误的 {{ unresolved.length + invalid.length }} 张不会上传；已成功图片会保留。</span>
        <div>
          <el-button :disabled="busy" @click="chooseMore">继续添加</el-button>
          <el-button v-if="failed.length" :disabled="busy" @click="downloadFailures">下载失败清单</el-button>
          <el-button v-if="failed.length" :disabled="busy" @click="start(true)">重试失败项 {{ failed.length }}</el-button>
          <el-button v-if="running" type="warning" :disabled="stopRequested" @click="stopRequested = true">{{ stopRequested ? '正在等待当前上传结束' : '停止后续上传' }}</el-button>
          <el-button :disabled="busy" @click="requestClose()">{{ items.length ? '关闭' : '取消' }}</el-button>
          <el-button type="primary" :disabled="busy || !ready.length" :loading="matching" @click="start(false)">开始导入 {{ ready.length }} 张</el-button>
        </div>
      </div>
    </template>

    <el-dialog v-model="assignVisible" title="指定 SPU" width="760px" append-to-body :close-on-click-modal="false">
      <p>将 {{ assignItems.length }} 张图片关联到同一个 SPU。可按编号、自定义款号或名称搜索。</p>
      <el-input v-model="searchKeyword" placeholder="输入款号或名称，按回车搜索" clearable @keyup.enter="searchSpus(1)">
        <template #append><el-button @click="searchSpus(1)">搜索</el-button></template>
      </el-input>
      <el-alert v-if="searchError" :title="searchError" type="error" :closable="false" />
      <el-table v-loading="searching" :data="spuOptions" highlight-current-row @current-change="selectedSpu = $event" @row-dblclick="applyAssignment">
        <el-table-column label="选择" width="55"><template #default="{ row }"><el-radio :model-value="selectedSpu?.id" :value="row.id" @change="selectedSpu = row"><span /></el-radio></template></el-table-column>
        <el-table-column label="图片" width="68"><template #default="{ row }"><el-image v-if="row.thumbnailPath" :src="assetUrl(row.thumbnailPath)" class="small-image" fit="contain" /></template></el-table-column>
        <el-table-column prop="productCode" label="SPU 编号" width="120" />
        <el-table-column prop="serialCode" label="自定义款号" width="145" show-overflow-tooltip />
        <el-table-column prop="productName" label="名称" min-width="140" show-overflow-tooltip />
        <el-table-column prop="imageCount" label="已有图片" width="85" />
      </el-table>
      <el-pagination v-model:current-page="searchPage" :page-size="20" :total="searchTotal" layout="total, prev, pager, next" @current-change="searchSpus" />
      <template #footer><el-button @click="assignVisible = false">取消</el-button><el-button type="primary" :disabled="!selectedSpu || searching" @click="applyAssignment">关联这 {{ assignItems.length }} 张图片</el-button></template>
    </el-dialog>
  </el-dialog>
</template>

<script>
import { matchSpuImages, searchImageSpus, uploadSpuImage } from '@/api/spuImageImport';
import { MAX_FILES, MAX_BATCH_SIZE, validateImageFile, groupImportImages, prepareImportRows, runImportQueue, failureCsv } from './spuImageImport.mjs';

export default {
  name: 'SpuImageImportDialog',
  emits: ['imported'],
  data() {
    return {
      visible: false, items: [], nextIndex: 0, pendingFiles: [], flushScheduled: false,
      matching: false, running: false, stopRequested: false, filter: 'all', error: '', notice: '', runItems: [],
      pageSize: 20, exceptionPageNumber: 1, groupPageSize: 8, groupPageNumber: 1, selectedPending: [],
      assignVisible: false, assignItems: [], selectedSpu: null, searchKeyword: '', spuOptions: [],
      searching: false, searchPage: 1, searchTotal: 0, searchError: '', searchGeneration: 0
    };
  },
  computed: {
    busy() { return this.matching || this.running; },
    matched() { return this.items.filter(i => i.matchState === 'matched'); },
    unresolved() { return this.items.filter(i => ['pending', 'matching'].includes(i.matchState)); },
    invalid() { return this.items.filter(i => i.matchState === 'invalid'); },
    ready() { return this.matched.filter(i => i.uploadState === 'waiting' && i.fileId); },
    succeeded() { return this.items.filter(i => i.uploadState === 'success'); },
    failed() { return this.items.filter(i => i.uploadState === 'failed'); },
    matchFailures() { return this.items.filter(i => i.matchFailed); },
    groups() { return groupImportImages(this.matched); },
    groupPage() { return this.groups.slice((this.groupPageNumber - 1) * this.groupPageSize, this.groupPageNumber * this.groupPageSize); },
    exceptionItems() { return this.items.filter(i => this.filter === 'invalid' ? i.matchState === 'invalid' : this.filter === 'pending' ? ['pending', 'matching'].includes(i.matchState) : i.matchState !== 'matched'); },
    exceptionPage() { return this.exceptionItems.slice((this.exceptionPageNumber - 1) * this.pageSize, this.exceptionPageNumber * this.pageSize); },
    runFinished() { return this.runItems.filter(i => ['success', 'failed'].includes(i.uploadState)).length; },
    overallProgress() { return this.runItems.length ? Math.floor(this.runItems.reduce((sum, i) => sum + (i.progress || 0), 0) / this.runItems.length) : 0; }
  },
  watch: {
    filter() { this.exceptionPageNumber = 1; this.groupPageNumber = 1; },
    'groups.length'() { this.groupPageNumber = Math.min(this.groupPageNumber, Math.max(1, Math.ceil(this.groups.length / this.groupPageSize))); },
    'exceptionItems.length'() { this.exceptionPageNumber = Math.min(this.exceptionPageNumber, Math.max(1, Math.ceil(this.exceptionItems.length / this.pageSize))); }
  },
  mounted() { window.addEventListener('beforeunload', this.beforeUnload); },
  beforeUnmount() { this.stopRequested = true; this.searchGeneration++; window.removeEventListener('beforeunload', this.beforeUnload); this.revokePreviews(); },
  methods: {
    open() { this.visible = true; },
    assetUrl(path) { return path ? process.env.VUE_APP_BASEURL.replace(/\/$/, '') + path : ''; },
    chooseMore() { this.$refs.picker?.$el.querySelector('input[type=file]')?.click(); },
    onFileChange(uploadFile) {
      if (!uploadFile.raw || this.busy) return;
      this.pendingFiles.push(uploadFile.raw);
      if (!this.flushScheduled) {
        this.flushScheduled = true;
        // Element Plus 在一次选择中逐个触发change，合并为一次匹配请求。
        this.$nextTick(() => this.flushFiles());
      }
    },
    async flushFiles() {
      const files = this.pendingFiles.splice(0);
      this.flushScheduled = false;
      this.$refs.picker?.clearFiles();
      let total = this.items.reduce((sum, i) => sum + i.size, 0);
      let skipped = 0;
      const added = [];
      for (const file of files) {
        if (this.items.length >= MAX_FILES || total + file.size > MAX_BATCH_SIZE) { skipped++; continue; }
        const reason = validateImageFile(file);
        const index = this.nextIndex++;
        const item = { clientId: 'image-' + index, index, file, name: file.name, size: file.size,
          preview: reason ? '' : URL.createObjectURL(file), reason, matchState: reason ? 'invalid' : 'matching',
          matchBy: '', matchFailed: false, sequence: null, spu: null, candidates: [], fileId: '', uploadToken: '',
          uploadState: 'waiting', progress: 0, error: '', attempted: false, prepared: false, isCover: false };
        this.items.push(item); total += file.size;
        // 使用Vue代理后的对象，异步更新才能驱动页面重绘。
        if (!reason) added.push(this.items[this.items.length - 1]);
      }
      this.notice = skipped ? `有 ${skipped} 张超过每批 500 张 / 500 MB 限制，未加入本批。` : '';
      await this.matchItems(added);
    },
    async matchItems(items) {
      if (!items.length) return;
      this.matching = true; this.error = '';
      items.forEach(i => { i.matchState = 'matching'; i.matchFailed = false; });
      try {
        const response = await matchSpuImages(items.map(i => ({ clientId: i.clientId, name: i.name, size: i.size })));
        if (response?.code !== 200 || !Array.isArray(response.data)) throw new Error(response?.content || '匹配失败，请重试');
        const results = new Map(response.data.map(r => [r.clientId, r]));
        for (const item of items) {
          const result = results.get(item.clientId);
          if (!result) throw new Error('匹配结果不完整，请重试');
          Object.assign(item, { matchState: result.status, reason: result.reason, matchBy: result.matchBy,
            sequence: result.sequence, candidates: result.candidates || [], fileId: result.fileId,
            uploadToken: result.uploadToken, spu: result.status === 'matched' ? result.candidates[0] : null });
        }
      } catch (error) {
        this.error = error?.message || error?.content || '匹配失败，请重试';
        items.filter(i => i.matchState === 'matching').forEach(i => { i.matchState = 'pending'; i.reason = this.error; i.matchFailed = true; });
      } finally { this.matching = false; }
    },
    openAssign(items) {
      if (this.busy || items.some(i => i.prepared)) return;
      this.assignItems = items; this.selectedSpu = null; this.searchKeyword = ''; this.assignVisible = true;
      this.searchSpus(1);
    },
    async searchSpus(page = 1) {
      const generation = ++this.searchGeneration;
      this.searchPage = page; this.searching = true; this.searchError = ''; this.selectedSpu = null;
      try {
        const response = await searchImageSpus(this.searchKeyword, page);
        if (response?.code !== 200) throw new Error(response?.content || 'SPU查询失败');
        if (generation === this.searchGeneration) { this.spuOptions = response.data.items; this.searchTotal = response.data.total; }
      } catch (error) {
        if (generation === this.searchGeneration) { this.spuOptions = []; this.searchTotal = 0; this.searchError = error?.message || error?.content || 'SPU查询失败'; }
      } finally { if (generation === this.searchGeneration) this.searching = false; }
    },
    applyAssignment() {
      if (!this.selectedSpu || this.searching) return;
      for (const item of this.assignItems) Object.assign(item, { spu: this.selectedSpu, matchState: 'matched', matchBy: 'manual', reason: '' });
      this.assignVisible = false; this.selectedPending = []; this.groupPageNumber = 1;
    },
    remove(item) {
      if (item.prepared || this.busy) return;
      if (item.preview) URL.revokeObjectURL(item.preview);
      this.items = this.items.filter(i => i.clientId !== item.clientId);
      this.selectedPending = this.selectedPending.filter(i => i.clientId !== item.clientId);
    },
    isDisplayImage(item, group) {
      const fresh = group.images.filter(i => !i.prepared);
      if (fresh.length) return fresh[0].clientId === item.clientId;
      const latestBatch = Math.max(...group.images.map(i => i.batch || 0));
      return item.batch === latestBatch && item.isCover;
    },
    matchLabel(item) { return { productCode: '匹配 SPU 编号', serialCode: '匹配自定义款号', manual: '人工指定' }[item.matchBy] || ''; },
    stateLabel(item) { return { waiting: '等待导入', uploading: '上传中', success: '导入成功', failed: '导入失败' }[item.uploadState]; },
    async start(retry) {
      if (this.busy) return;
      const rows = prepareImportRows(retry ? this.failed : this.ready);
      if (!rows.length) return;
      this.runItems = rows; this.stopRequested = false; this.running = true; this.error = '';
      rows.forEach(i => { i.uploadState = 'waiting'; i.progress = 0; });
      try {
        await runImportQueue(rows, async (item, progress) => {
          try {
            const response = await uploadSpuImage(item, progress);
            if (response?.code !== 200) throw new Error(response?.content || '网络连接中断，请重试');
          } catch (error) {
            const message = error?.message || error?.content || '网络连接中断，请重试';
            if ([401, 403].includes(error?.status) || /无权限|登录/.test(message)) { this.stopRequested = true; this.error = message; }
            throw new Error(message);
          }
        }, { shouldStop: () => this.stopRequested });
      } finally {
        this.running = false;
        // 刷新所有已提交结果，包含服务端已成功但客户端响应超时的请求。
        this.$emit('imported');
      }
    },
    downloadFailures() {
      const url = URL.createObjectURL(new Blob([failureCsv(this.items)], { type: 'text/csv;charset=utf-8;' }));
      const link = document.createElement('a'); link.href = url; link.download = 'SPU图片导入失败清单.csv'; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
    beforeUnload(event) {
      if (this.visible && (this.busy || this.items.some(i => i.uploadState !== 'success'))) { event.preventDefault(); event.returnValue = ''; }
    },
    async requestClose(done) {
      if (this.busy) return;
      if (this.items.some(i => i.uploadState !== 'success')) {
        try { await this.$confirm('关闭后未处理图片需要重新选择。已成功导入的图片会保留，是否关闭？', '关闭批量导入', { type: 'warning' }); }
        catch { return; }
      }
      this.assignVisible = false;
      if (typeof done === 'function') done(); else this.visible = false;
    },
    revokePreviews() { this.items.forEach(i => { if (i.preview) URL.revokeObjectURL(i.preview); }); },
    reset() {
      this.revokePreviews(); this.items = []; this.runItems = []; this.pendingFiles = []; this.nextIndex = 0;
      this.error = ''; this.notice = ''; this.filter = 'all'; this.selectedPending = []; this.stopRequested = false;
      this.$refs.picker?.clearFiles();
    }
  }
};
</script>

<style scoped>
.import-content { max-width: 1440px; margin: 0 auto; padding-bottom: 85px; }
.import-content > * + * { margin-top: 18px; }
.compact-picker :deep(.el-upload-dragger) { padding: 12px; }
.summary-bar, .section-heading, .filter-bar, .import-footer { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; }
.summary-bar { color: #606266; padding: 8px 0; }
.summary-bar b { color: #303133; font-size: 20px; }
.section-heading h3 { margin: 0; font-size: 16px; }
.exception-panel, .spu-group { border: 1px solid #e4e7ed; border-radius: 8px; padding: 16px; margin-bottom: 18px; }
.exception-panel { background: #fffcf5; }
.spu-heading { display: flex; gap: 12px; align-items: center; line-height: 1.7; }
.section-heading > span { color: #909399; font-size: 13px; }
.small-image { width: 48px; height: 48px; }
.image-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(175px, 1fr)); gap: 14px; margin-top: 15px; }
.image-card { min-width: 0; border: 1px solid #ebeef5; border-radius: 6px; padding: 10px; background: white; }
.import-preview { width: 100%; height: 140px; background: #f5f7fa; }
.file-name { margin: 8px 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.match-label { display: block; font-size: 12px; color: #909399; margin: 6px 0; }
.upload-status { font-size: 13px; color: #909399; margin-top: 8px; }
.upload-status.success { color: #529b2e; }
.upload-status.failed, .file-error { color: #c45656; }
.file-error { font-size: 12px; overflow-wrap: anywhere; margin: 6px 0; }
.image-actions { display: flex; justify-content: space-between; margin-top: 8px; }
.batch-progress > span { font-size: 13px; color: #606266; }
.import-footer { text-align: left; padding: 12px 24px; }
.import-footer > span { font-size: 13px; color: #909399; }
:global(.spu-image-import .el-dialog__footer) { position: sticky; bottom: 0; z-index: 5; background: white; border-top: 1px solid #ebeef5; padding: 0; }
:global(.spu-image-import .el-dialog__body) { padding-top: 12px; }
</style>
